import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { useAuth } from "@/context/AuthContext";
import { Loader2, Lock } from "lucide-react";

function formatError(detail) {
  if (detail == null) return "Something went wrong. Please try again.";
  if (typeof detail === "string") return detail;
  if (Array.isArray(detail))
    return detail.map((e) => (e?.msg ? e.msg : JSON.stringify(e))).join(" ");
  if (detail?.msg) return detail.msg;
  return String(detail);
}

export default function Login() {
  const { login } = useAuth();
  const navigate = useNavigate();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    setLoading(true);
    try {
      await login(email, password);
      navigate("/admin");
    } catch (err) {
      setError(formatError(err.response?.data?.detail) || err.message);
    } finally {
      setLoading(false);
    }
  };

  const inputCls =
    "w-full bg-transparent border-b border-white/20 py-3 text-base text-[var(--cream)] placeholder:text-[var(--cream)]/40 focus:outline-none focus:border-[var(--gold)] transition-colors";

  return (
    <div className="relative min-h-screen grain overflow-hidden bg-[var(--charcoal)] flex items-center justify-center px-6">
      <div className="beam-glow pointer-events-none absolute inset-0" />
      <motion.div
        initial={{ opacity: 0, y: 26 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.8, ease: [0.22, 1, 0.36, 1] }}
        className="relative z-10 w-full max-w-sm"
        data-testid="login-page"
      >
        <div className="flex items-center gap-2 text-[var(--cream)]">
          <span className="serif text-2xl">Beam</span>
          <span className="serif text-2xl gold-shimmer">Concierge</span>
        </div>
        <p className="mt-6 overline text-[var(--gold-soft)] flex items-center gap-2">
          <Lock size={13} /> Private Concierge Desk
        </p>
        <h1 className="mt-3 serif text-3xl text-[var(--cream)]">Welcome back.</h1>

        <form onSubmit={submit} className="mt-9 space-y-6">
          <div>
            <label className="overline block mb-1.5 text-[var(--cream)]/60">Email</label>
            <input
              data-testid="login-email"
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder="you@beamconcierge.com"
              className={inputCls}
              autoComplete="username"
            />
          </div>
          <div>
            <label className="overline block mb-1.5 text-[var(--cream)]/60">Password</label>
            <input
              data-testid="login-password"
              type="password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              placeholder="••••••••"
              className={inputCls}
              autoComplete="current-password"
            />
          </div>

          {error && (
            <p data-testid="login-error" className="text-sm text-red-300">
              {error}
            </p>
          )}

          <button
            type="submit"
            disabled={loading}
            data-testid="login-submit"
            className="w-full inline-flex items-center justify-center gap-2 bg-[var(--gold)] px-6 py-3.5 text-sm text-[var(--charcoal)] hover:bg-[var(--gold-light)] transition-colors disabled:opacity-60"
          >
            {loading ? <><Loader2 size={15} className="animate-spin" /> Signing in…</> : "Enter"}
          </button>
        </form>
      </motion.div>
    </div>
  );
}
